// lib/microsite/couponUx.ts
//
// Reglas PURAS del cupón en el carrito del micrositio (spec §7.2). Sin React,
// sin fetch: solo las decisiones que gobiernan lo que el cliente VE y lo que
// viaja a `/api/online-orders/session`. Vive aparte de los componentes porque
// son las dos preguntas que no pueden equivocarse —
//
//   1. ¿cuánto descuento pinto? y
//   2. ¿mando el código al cobro?
//
// — y una respuesta mal derivada dentro de un JSX de 700 líneas es exactamente
// cómo se llega a "vio un precio y pagó otro".
//
// 🔴 NADA de acá decide dinero. `/api/online-orders/session` recomputa el
// subtotal desde precios de la BD y revalida el cupón contra la fila viva antes
// de cobrar. Esto es la vista previa; aquello es la autoridad.

import { computeCouponDiscount, normalizeCouponCode, type CouponDiscountType } from "@/lib/coupons/engine"
import { snapshotHasScope, type MaybeScopedCoupon } from "@/lib/coupons/scopeTripwire"
import { couponReasonKey, type CouponReasonKey } from "@/lib/kiosk/couponReasonMessage"
import { effectiveItemCardPrice, effectiveUnitPrice, salesTax, type PricingConfig } from "@/lib/utils/pricing"
import { retailLineTotal } from "@/lib/retail/lineTotal"
import { retailWeightLabel } from "@/lib/retail/weightLabel"
import {
  requestCouponPreview,
  type CouponPreviewInput,
  type CouponPreviewResult,
} from "@/lib/coupons/previewClient"
import { couponAlcoholFactsOf, couponBaseFromLines, lineIsCouponAlcohol } from "@/lib/coupons/alcoholExclusion"
import { sanitizeCouponRequestLines, type CouponRequestLines } from "@/lib/coupons/requestLines"
import type { CartItem as MicrositeCartItem } from "@/components/microsite/types"

/** Cupón que el cliente aplicó en el carrito (lo que devolvió el preview). */
export interface AppliedCoupon {
  /** Ya normalizado (^[A-Z0-9]{4,20}$) por `normalizeCouponCode`. */
  code: string
  discountType: CouponDiscountType
  discountValue: number
  /**
   * Compra mínima del cupón, o null si no pide. Se guarda para poder responder
   * localmente "te faltan $X" cuando el cliente quita un plato — sin esto habría
   * que re-validar contra el servidor en cada toque del carrito, o peor: dejar
   * el descuento pintado hasta que /session lo tumbe en el paso de pago.
   */
  minSubtotal: number | null
  /**
   * A1b (TABC): ítems y opciones de alcohol del LOCAL que devolvió el preview.
   * El carrito los usa (micrositeCouponBase) para que el cupón no rebaje esas
   * líneas, igual que /session. Ausentes = sin alcohol marcado: el cálculo de
   * main. Solo viajan si no están vacíos.
   */
  alcoholItemIds?: string[]
  alcoholOptionIds?: string[]
}

export interface CouponDisplayState {
  /** Dólares a restar del subtotal ANTES del impuesto (espeja lib/online-orders/pricing). */
  discount: number
  /** El carrito ya no alcanza el mínimo: el cupón queda EN PAUSA, no aplicado. */
  belowMinimum: boolean
  /** Cuánto falta para volver a alcanzar el mínimo. 0 cuando `belowMinimum` es false. */
  missingForMinimum: number
  /**
   * Único gate de si `couponCode` viaja en el body de `/session`. Mandarlo con
   * el carrito por debajo del mínimo garantiza un 422 `coupon_rejected` — el
   * cliente vería un error justo donde va a pagar, por algo que la pantalla ya
   * sabía. Se calla el código y el carrito explica por qué.
   */
  sendToSession: boolean
  /**
   * A1b: el cupón dejó fuera alguna línea de alcohol («Cupón no aplica a
   * productos con alcohol»). SOLO presente cuando es true.
   */
  couponExcludedAlcohol?: true
}

/**
 * Estado del cupón para un subtotal dado. `subtotal` es el de ítems SIN
 * descuento (base tarjeta), el mismo número contra el que el servidor compara
 * `min_subtotal` — no el subtotal ya descontado.
 *
 * A1b (TABC): `couponBase` es ese subtotal SIN las líneas de alcohol
 * (micrositeCouponBase) — el mínimo y el descuento se miden sobre ella, como en
 * /session. `couponExcludedAlcohol` es su `excluded`. Con los defaults (base =
 * subtotal, false) el resultado es el de main, mismo objeto.
 */
export function couponDisplayState(
  coupon: AppliedCoupon | null,
  subtotal: number,
  couponBase: number = subtotal,
  couponExcludedAlcohol: boolean = false,
): CouponDisplayState {
  const none: CouponDisplayState = {
    discount: 0,
    belowMinimum: false,
    missingForMinimum: 0,
    sendToSession: false,
  }
  if (!coupon) return none

  // PR-2a — riel PRE-cobro del MICROSITIO, en el navegador. El ambito viaja por
  // el snapshot del servidor (spec D-11) pero este riel todavia no sabe
  // computarlo (eso es PR-2b): pintar el descuento saldria del subtotal
  // COMPLETO y le prometeria al cliente un numero que /session no va a
  // respetar. Rechazar aca es seguro — nadie pago todavia — y ademas calla el
  // codigo (`sendToSession: false`) para que el 422 no lo sorprenda EN la
  // pantalla de pago.
  //
  // El cast es la forma de decir "en runtime esto puede traer claves que el tipo
  // no declara": `AppliedCoupon` lo arma `runCouponApply` con lo que devuelve el
  // preview, que HOY no trae ambito — cuando PR-2b lo enhebre hasta aca, este
  // guard ya esta puesto. `snapshotHasScope` es LAXO (`!= null`) justamente para
  // poder servir a este riel; ver su docstring en lib/coupons/scopeTripwire.ts.
  if (snapshotHasScope(coupon as unknown as MaybeScopedCoupon)) return none

  // A1b: la bandera viaja en los tres estados de un cupón puesto (pausa,
  // aplicado, $0 por todo alcohol), para que la pantalla explique por qué.
  const alcoholFlag = couponExcludedAlcohol ? { couponExcludedAlcohol: true as const } : {}

  // `<` estricto, igual que `checkCouponRestrictions`: subtotal === min_subtotal
  // SÍ alcanza. Un `<=` acá rechazaría en pantalla un cupón que el servidor
  // acepta, y el cliente perdería el descuento sin que nadie sepa por qué.
  // A1b: medido sobre la base del cupón (sin alcohol), como /session.
  if (coupon.minSubtotal !== null && couponBase < coupon.minSubtotal) {
    return {
      discount: 0,
      belowMinimum: true,
      missingForMinimum: Math.round((coupon.minSubtotal - couponBase) * 100) / 100,
      sendToSession: false,
      ...alcoholFlag,
    }
  }

  // MISMA función pura que usa el servidor al cobrar (lib/coupons/engine), con
  // el mismo redondeo: el descuento mostrado y el cobrado salen del mismo
  // código, no de dos implementaciones que derivan.
  const discount = computeCouponDiscount(
    { discount_type: coupon.discountType, discount_value: coupon.discountValue },
    couponBase,
  )

  // Carrito vacío (o descuento 0): no hay nada que descontar y /session
  // rechazaría con `coupon_requires_items`. El cupón sigue guardado — vuelve
  // solo en cuanto el cliente agregue algo. A1b: un carrito TODO alcohol cae
  // acá (base 0) y /session lo rechazaría con `alcohol_only`: se calla igual.
  if (discount <= 0) return { ...none, discount: 0, ...alcoholFlag }

  return { discount, belowMinimum: false, missingForMinimum: 0, sendToSession: true, ...alcoholFlag }
}

// ─── Subtotal y base del cupón (A1b) ─────────────────────────────────────────
//
// UNA expresión por línea para el subtotal que se muestra, la base del cupón, las
// `lines` del preview, el «Ver carrito · $X» del micrositio y el renglón del
// carrito: si fueran varias, la base podría restar un número que el subtotal no
// sumó. Es la de lib/online-orders/pricing (la autoridad, /session):
// (precio base efectivo + modificadores) × cantidad; por peso, precio/unidad × peso.

/**
 * Aporte de UNA línea del carrito del micrositio. DISPLAY: el precio BASE se
 * muestra redondeado a tarjeta (online siempre es tarjeta); los modificadores
 * (price_delta) NUNCA se redondean; sin redondear la línea. El cobro real lo
 * recalcula el servidor en /api/online-orders/session.
 *
 * Súper online: una línea por peso (`weightMilli` presente, nunca en un
 * restaurante) se cotiza como el servidor — precio por libra/kilo SIN el redondeo
 * de $0,50 (`effectiveUnitPrice`) × peso, redondeada una vez, sin modificadores
 * ni cantidad. Una variante no necesita rama: `price` es el precio crudo de la
 * variante (R5) y el servidor la cobra por el mismo camino que un plato.
 */
export function micrositeLineSubtotal(item: MicrositeCartItem, pricingCfg: PricingConfig): number {
  if (item.weightMilli != null) {
    return retailLineTotal({
      unitPrice: effectiveUnitPrice(item.price, pricingCfg),
      modifiersTotal: 0,
      quantity: 1,
      weight: item.weightMilli / 1000,
    })
  }
  const base = effectiveItemCardPrice(item.price, pricingCfg)
  const modSum = item.modifiers.reduce((m, mod) => m + mod.price_delta * (mod.quantity ?? 1), 0)
  return (base + modSum) * item.quantity
}

/** Idioma de la tienda (next-intl): decide el separador decimal del peso. */
export type MicrositeLocale = "es" | "en"

/**
 * Peso de una línea para mostrar, según el idioma de la tienda (R5): coma en
 * español (1500 lb → «1,5 lb»), punto en inglés («1.5 lb»). Sin idioma, español.
 */
export function micrositeWeightLabel(
  weightMilli: number,
  unit: MicrositeCartItem["weightUnit"],
  locale: MicrositeLocale = "es",
): string {
  return retailWeightLabel(weightMilli, unit, locale)
}

/**
 * Nombre de la línea que ve el cliente (carrito) y que va a North (R5): con el
 * sufijo de la variante («Leche · Galón») o el peso («Banana · 1,5 lb»). Una
 * línea de restaurante (sin peso ni variante) devuelve `name` tal cual.
 */
export function micrositeLineName(
  item: Pick<MicrositeCartItem, "name" | "weightMilli" | "weightUnit" | "variantId" | "variantName">,
  locale: MicrositeLocale = "es",
): string {
  if (item.weightMilli != null) return `${item.name} · ${micrositeWeightLabel(item.weightMilli, item.weightUnit, locale)}`
  if (item.variantId != null && item.variantName) return `${item.name} · ${item.variantName}`
  return item.name
}

/**
 * Producto del formulario de North para UNA línea (solo display: el cobro real
 * lo re-precia el servidor). Restaurante: base de tarjeta redondeada + deltas
 * exactos, × cantidad (lo de main). Por peso: el peso va en el nombre (con el
 * separador del idioma), el precio es el total de la línea y la cantidad 1 — un
 * «2,5 × $3,99» no cabe en un entero. Variante: el nombre lleva su sufijo.
 */
export function micrositeNorthProduct(
  item: MicrositeCartItem,
  pricingCfg: PricingConfig,
  locale: MicrositeLocale = "es",
): { name: string; price: number; quantity: number } {
  if (item.weightMilli != null) {
    return {
      name: micrositeLineName(item, locale),
      price: micrositeLineSubtotal(item, pricingCfg),
      quantity: 1,
    }
  }
  return {
    // Restaurante: `name` tal cual. Variante: con su sufijo (mismo precio y cantidad que un plato).
    name: micrositeLineName(item, locale),
    price: +(effectiveItemCardPrice(item.price, pricingCfg) + item.modifiers.reduce((s, m) => s + m.price_delta * (m.quantity ?? 1), 0)).toFixed(2),
    quantity: item.quantity,
  }
}

/** Subtotal de ítems SIN descuento (el de OrderSheet), redondeado a centavo. */
export function micrositeCartSubtotal(cart: readonly MicrositeCartItem[], pricingCfg: PricingConfig): number {
  const sub = cart.reduce((s, i) => s + micrositeLineSubtotal(i, pricingCfg), 0)
  return Math.round(sub * 100) / 100
}

/**
 * Súper online (revisión final PR 2b, I2): subtotal GRAVABLE del carrito, con la
 * misma regla que el servidor (`priceOnlineOrder`): una línea paga impuesto salvo
 * que su producto tenga `is_taxable === false` (null o producto ausente = gravable).
 *
 * La señal es la CLAVE `is_taxable` en las filas del menú: el loader la pide solo
 * con `online_retail_v2`. Sin ninguna fila que la traiga (todo restaurante hoy)
 * devuelve `undefined` y `micrositeOrderTotals` hace la cuenta de main. Los
 * restaurantes con productos exentos NO cambian en este PR (cambiaría lo que ven).
 */
export function micrositeTaxableSubtotal(
  cart: readonly MicrositeCartItem[],
  menuItems: ReadonlyArray<{ id: string; is_taxable?: boolean | null }>,
  pricingCfg: PricingConfig,
): number | undefined {
  if (!menuItems.some((m) => Object.hasOwn(m, "is_taxable"))) return undefined
  const exempt = new Set(menuItems.filter((m) => m.is_taxable === false).map((m) => m.id))
  const sum = cart.reduce((s, i) => (exempt.has(i.itemId) ? s : s + micrositeLineSubtotal(i, pricingCfg)), 0)
  return Math.round(sum * 100) / 100
}

/**
 * Base del cupón: `subtotal` sin las líneas de alcohol, con las listas del LOCAL
 * que guardó el cupón aplicado (una cerveza agregada después también queda
 * fuera). Sin cupón, sin listas o sin alcohol en el carrito devuelve `subtotal`
 * intacto y `excluded: false` — el camino de main.
 */
export function micrositeCouponBase(
  cart: readonly MicrositeCartItem[],
  pricingCfg: PricingConfig,
  subtotal: number,
  coupon: Pick<AppliedCoupon, "alcoholItemIds" | "alcoholOptionIds"> | null,
): { couponBase: number; excluded: boolean } {
  const facts = couponAlcoholFactsOf(coupon)
  if (!facts) return { couponBase: subtotal, excluded: false }
  return couponBaseFromLines(
    subtotal,
    cart.map((i) => ({
      lineSubtotal: micrositeLineSubtotal(i, pricingCfg),
      isAlcohol: lineIsCouponAlcohol(
        { item_id: i.itemId, optionIds: i.modifiers.map((m) => m.modifier_option_id) },
        facts,
      ),
    })),
  )
}

/**
 * `lines` del preview (/api/online-orders/coupon-preview), con la misma
 * expresión por línea que el subtotal. `undefined` si el esquema de la ruta las
 * rechazaría: entonces el preview va sin líneas, como en main.
 */
export function micrositeCouponRequestLines(
  cart: readonly MicrositeCartItem[],
  pricingCfg: PricingConfig,
): CouponRequestLines {
  return sanitizeCouponRequestLines(
    cart.map((i) => ({
      item_id: i.itemId,
      option_ids: i.modifiers.map((m) => m.modifier_option_id),
      line_subtotal: micrositeLineSubtotal(i, pricingCfg),
    })),
  )
}

/** Un rechazo de cupón devuelto por `/api/online-orders/session`. */
export interface SessionCouponRejection {
  /** Clave de copy bajo `public.onlineOrder.checkout.coupon.reason.*`. */
  reasonKey: CouponReasonKey
  /** Mínimo que pide el cupón, cuando el rechazo fue por no alcanzarlo. */
  minSubtotal: number | null
}

/**
 * ¿La respuesta de `/session` es un rechazo DEL CUPÓN? (spec §7.2 / P1-18).
 *
 * Este es el caso que gobierna toda la pantalla: entre que el cliente aplicó el
 * cupón en el carrito y llegó a pagar, el mundo cambió — se agotó porque otro
 * lo canjeó, venció, o el carrito dejó de alcanzar el mínimo. `/session`
 * responde 422 y NO cobra nada (el rechazo llega ANTES de crear la sesión de
 * pago). Devolver aquí un valor no-nulo es lo que le dice a la interfaz
 * "suelta el cupón, vuelve al carrito y explícalo" en vez de mostrar un error
 * de pago incomprensible o, peor, cobrar callada el total sin descuento.
 *
 * Devuelve null para cualquier otro fallo: un problema del carrito, de la red o
 * de North NO es culpa del cupón, y tratarlo como tal le quitaría al cliente un
 * descuento que sigue siendo válido.
 */
export function parseSessionCouponRejection(
  status: number,
  body: unknown,
): SessionCouponRejection | null {
  if (status !== 422) return null
  const data = body as { error?: unknown; reason?: unknown; minSubtotal?: unknown } | null
  if (!data) return null

  // `coupon_requires_items`: el cliente legacy mandó couponCode sin `items`.
  // Este micrositio SIEMPRE manda items, así que solo se alcanza con una
  // pestaña vieja abierta desde antes del deploy — se trata igual (soltar el
  // cupón y seguir) en vez de dejarla atrapada sin poder pagar.
  if (data.error !== "coupon_rejected" && data.error !== "coupon_requires_items") return null

  const minSubtotal = typeof data.minSubtotal === "number" ? data.minSubtotal : null
  const reason = typeof data.reason === "string" ? data.reason : null
  return {
    // `couponReasonKey` es el mismo mapeo que usan el kiosko y la pantalla del
    // cliente. Sin `reason` — `coupon_requires_items`, o el 422 que
    // `priceOnlineOrder` emite en modo estricto sin reason tipado — se cae a
    // `unavailable` ("ahora no se puede aplicar"), NO al default `notFound` de
    // esa función ("revisa el código"): el cliente no tecleó nada mal y
    // mandarlo a corregir un código correcto es peor que no explicarle.
    reasonKey: reason ? couponReasonKey(reason) : "unavailable",
    minSubtotal,
  }
}

// ─── Totales del carrito ─────────────────────────────────────────────────────
//
// Vive acá y no dentro del `useMemo` de OrderSheet porque es matemática de
// dinero y tiene que poder probarse de verdad: este repo no tiene jsdom, así
// que nada que viva dentro de un componente se puede ejercitar. El orden de las
// operaciones es el que importa y es el mismo del servidor
// (lib/online-orders/pricing): el descuento baja el subtotal ANTES del
// impuesto. Gravar el subtotal completo cobraría impuesto sobre dinero que el
// cliente no paga.

export interface MicrositeOrderTotalsInput {
  /** Ítems SIN descuento, en base tarjeta. */
  subtotal: number
  /** Dólares del cupón, ya derivados por `couponDisplayState`. */
  couponDiscount: number
  taxRate: number
  cashPerTxFee: number
  processingFeeEnabled: boolean
  /** Envío del courier. NUNCA se grava acá: entra al total en centavos enteros. */
  deliveryFee: number
  /** Propina al repartidor en CENTAVOS enteros. Sin impuesto ni fee. Opcional: 0. */
  driverTipCents?: number
  /** Precios con impuesto incluido (locations.prices_include_tax): el impuesto se
   *  EXTRAE del subtotal descontado y el total no lo suma. Opcional: false. */
  pricesIncludeTax?: boolean
  /** Súper online (I2): subtotal gravable SIN descuento (`micrositeTaxableSubtotal`).
   *  undefined (todo restaurante) = se grava el subtotal descontado entero, como main. */
  taxableSubtotal?: number
}

export interface MicrositeOrderTotals {
  discountedSubtotal: number
  taxAmount: number
  processingFee: number
  total: number
  /** true: `taxAmount` ya está DENTRO del total (se muestra "incluido"). */
  pricesIncludeTax: boolean
}

export function micrositeOrderTotals(i: MicrositeOrderTotalsInput): MicrositeOrderTotals {
  const discountedSubtotal = Math.round((i.subtotal - i.couponDiscount) * 100) / 100
  const pricesIncludeTax = i.pricesIncludeTax === true
  // Misma función de impuesto que el servidor (lib/online-orders/pricing): con
  // impuesto encima es round(subtotal descontado × tasa), como siempre.
  // Súper online (I2): con `taxableSubtotal`, el prorrateo del servidor —base =
  // subtotal descontado × (gravable / subtotal), sin redondeo intermedio—. Sin él,
  // la línea de main tal cual.
  const tax =
    i.taxableSubtotal === undefined
      ? salesTax(discountedSubtotal, i.taxRate, pricesIncludeTax)
      : salesTax(
          discountedSubtotal * (i.subtotal > 0 ? i.taxableSubtotal / i.subtotal : 1),
          i.taxRate,
          pricesIncludeTax,
        )
  const taxAmount = tax.taxAmount
  const processingFee = i.processingFeeEnabled ? Math.round(i.cashPerTxFee * 100) / 100 : 0
  // Misma construcción que el servidor: se cierra el base en centavos ENTEROS y
  // recién ahí se suman los centavos del envío (sin polvo de punto flotante).
  const baseCents = Math.round((discountedSubtotal + tax.taxOnTop + processingFee) * 100)
  const total = (baseCents + Math.round(i.deliveryFee * 100) + (i.driverTipCents ?? 0)) / 100
  return { discountedSubtotal, taxAmount, processingFee, total, pricesIncludeTax }
}

// ─── Aplicar un cupón (teclado o deep link) ──────────────────────────────────
//
// Los dos caminos —el cliente teclea, o el enlace/QR trae `?coupon=`— pasan por
// acá para que no puedan divergir. Fuera del componente por el mismo motivo que
// los totales: es lo único que se puede probar de punta a punta sin DOM.

export type CouponApplyOutcome =
  | { status: "applied"; coupon: AppliedCoupon }
  | {
      status: "rejected"
      reasonKey: CouponReasonKey
      minSubtotal: number | null
      /** A1b: base SIN alcohol del mínimo (solo si el servidor excluyó algo). */
      measuredSubtotal?: number
    }

export interface RunCouponApplyInput {
  locationId: string
  /** Código CRUDO (tecleado o del deep link). Se normaliza aguas abajo. */
  code: string
  subtotal: number
  /** A1b: líneas del carrito (micrositeCouponRequestLines). Ausente = body de main. */
  lines?: CouponRequestLines
  /** Inyectable para test; por defecto el cliente real del preview público. */
  preview?: (input: CouponPreviewInput) => Promise<CouponPreviewResult>
}

export async function runCouponApply(input: RunCouponApplyInput): Promise<CouponApplyOutcome> {
  const preview = input.preview ?? requestCouponPreview
  const res = await preview({
    locationId: input.locationId,
    code: input.code,
    subtotal: input.subtotal,
    ...(input.lines ? { lines: input.lines } : {}),
  })
  if (!res.ok) {
    return {
      status: "rejected",
      // SIEMPRE el motivo específico del servidor: "ya venció" y "no llega al
      // mínimo" mandan al cliente a hacer cosas distintas.
      reasonKey: couponReasonKey(res.reason),
      minSubtotal: typeof res.minSubtotal === "number" ? res.minSubtotal : null,
      ...(typeof res.measuredSubtotal === "number" ? { measuredSubtotal: res.measuredSubtotal } : {}),
    }
  }
  return {
    status: "applied",
    coupon: {
      code: res.code,
      discountType: res.discount_type,
      discountValue: res.discount_value,
      minSubtotal: res.min_subtotal,
      // A1b: las listas del local, SOLO si vinieron (previewClient ya las omite
      // vacías): sin alcohol marcado el cupón guardado es el objeto de main.
      ...(res.alcoholItemIds || res.alcoholOptionIds
        ? { alcoholItemIds: res.alcoholItemIds ?? [], alcoholOptionIds: res.alcoholOptionIds ?? [] }
        : {}),
    },
  }
}

// ─── Deep link `?coupon=CÓDIGO` ──────────────────────────────────────────────

/**
 * Valor inicial del campo cuando la visita llegó por el enlace o el QR que el
 * panel de cupones reparte.
 *
 * Se usa como inicializador de `useState` (no se escribe desde un efecto) para
 * que el código quede EN EL MARKUP del primer render: sin eso, lo único
 * observable del deep link vive dentro de un efecto y este repo no tiene forma
 * de ejercitarlo.
 */
export function deepLinkInitialInput(pendingCouponCode: string | null | undefined): string {
  if (!pendingCouponCode) return ""
  return normalizeCouponCode(pendingCouponCode) ?? pendingCouponCode.trim().toUpperCase()
}

export type DeepLinkDecision = "apply" | "consume" | "wait" | "none"

/**
 * Qué hacer con el código pendiente del deep link.
 *
 *  - `none`   — no hay código, o el local no tiene cupones.
 *  - `consume`— ya hay un cupón puesto: se descarta el pendiente sin pedir nada.
 *  - `wait`   — carrito vacío. Con subtotal 0 un cupón con mínimo se rechazaría
 *               a sí mismo antes de que el cliente eligiera su primer plato.
 *  - `apply`  — se valida contra el servidor y se consume EN EL MISMO intento,
 *               salga bien o mal: reintentarlo en cada apertura del carrito
 *               haría que un código vencido gritara su error una y otra vez,
 *               sin forma de callarlo.
 */
export function deepLinkDecision(input: {
  pendingCouponCode: string | null | undefined
  couponsEnabled: boolean
  locationId: string | null | undefined
  appliedCoupon: AppliedCoupon | null
  cartIsEmpty: boolean
}): DeepLinkDecision {
  if (!input.pendingCouponCode) return "none"
  if (!input.couponsEnabled || !input.locationId) return "none"
  if (input.appliedCoupon) return "consume"
  if (input.cartIsEmpty) return "wait"
  return "apply"
}
